import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { PartnerFeature, UnionPartnerFeature } from "src/api/public-types";
import { useI18n } from "src/hooks/useI18n";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import usePathSegment from "src/hooks/usePathSegment";
import { useValidRoutes } from "src/hooks/useValidRoutes";
import pagePath from "src/pagePath.json";
import { partnerMemberState } from "src/recoil/partnerMember/atom";
import { globalPartnerState } from "src/recoil/partners/atom";
import { PartnerPermission, PartnerPrivilegeCode, PrivilegeCodeMenus, UnionPartnerPrivilegeCode } from "src/types/partner";
import { ViewYmdFormat } from "src/utils";
import { isMobilePath } from "src/utils/common-util";
export type Menu = {
  title: string;
  icon?: string;
  url?: string;
  openMenu?: boolean;
  twoDepth?: Menu[];
  divider?: boolean;
  active?: boolean;
  feature?: UnionPartnerFeature;
  privilege?: UnionPartnerPrivilegeCode;
};

export const SideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const partner = useRecoilValue(globalPartnerState);
  const partnerMember = useRecoilValue(partnerMemberState);
  const { partnerPrivileges, fetchAuthorityReadonly, authorityStatus } = usePartnerAuthority();
  const { isAuthenticated } = useAuth0();
  const { t } = useI18n();
  const currentMenu = usePathSegment();

  const [sideBarMenuList, setSideBarMenuList] = useState<Menu[]>([
    {
      title: t("lnb.sideBar.body.facilities.link"),
      icon: "building",
      openMenu: true,
      twoDepth: [
        {
          title: t("lnb.sideBar.body.building.link"),
          url: pagePath.building.list,
          privilege: PartnerPrivilegeCode.BUILDING,
        },
        {
          title: t("lnb.sideBar.body.accessDevice.link"),
          url: pagePath.accessDevice.list,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.cctv.link"),
          url: pagePath.cctv.list,
          feature: PartnerFeature.CCTV,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.iotConfig.link"),
          url: pagePath.iotConfig.list,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.monitoring.link"),
          icon: "monitoring",
          url: pagePath.monitoring,
          privilege: PartnerPrivilegeCode.BASIC,
        },
      ],
      divider: true,
    },

    {
      title: t("lnb.sideBar.body.plans.link"),
      icon: "product",
      openMenu: true,
      twoDepth: [
        {
          title: t("lnb.sideBar.body.product.link"),
          url: pagePath.product.list,
          privilege: PartnerPrivilegeCode.PRODUCT,
        },
        {
          title: t("lnb.sideBar.body.price.link"),
          icon: "price",
          url: pagePath.price.list,
          feature: PartnerFeature.RESERVATION,
          privilege: PartnerPrivilegeCode.BASIC,
        },
      ],
      divider: true,
    },
    {
      title: t("lnb.sideBar.body.contracts.link"),
      icon: "provider",
      openMenu: true,
      twoDepth: [
        //prod 환경에서는 계약그룹 메뉴 미노출 > 1.28.1 에 노출
        {
          title: t("lnb.sideBar.body.contractGroup.link"),
          icon: "contractGroup",
          url: pagePath.contractGroup.list,
          privilege: PartnerPrivilegeCode.CONTRACT,
        },

        {
          title: t("lnb.sideBar.body.contract.link"),
          icon: "contract",
          url: pagePath.contract.list,
          privilege: PartnerPrivilegeCode.CONTRACT,
        },
        {
          title: t("lnb.sideBar.body.contract-s2.link"),
          icon: "contract",
          url: pagePath.contractS2.list,
          privilege: PartnerPrivilegeCode.CONTRACT_S2,
        },
        {
          title: t("lnb.sideBar.body.billing.link"),
          icon: "billing",
          url: pagePath.billing.list,
          privilege: PartnerPrivilegeCode.CONTRACT,
          divider: true,
        },
        {
          title: t("lnb.sideBar.body.billing-s2.link"),
          icon: "billing",
          url: pagePath.billingS2.list,
          privilege: PartnerPrivilegeCode.CONTRACT_S2,
          divider: true,
        },
        {
          title: t("lnb.sideBar.body.org.link"),
          icon: "org",
          url: pagePath.organization.list,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.provider.link"),
          url: pagePath.provider.list,
          privilege: PartnerPrivilegeCode.BASIC,
        },
      ],
      divider: true,
    },
    {
      title: t("lnb.sideBar.body.people.link"),
      icon: "user",
      openMenu: true,
      twoDepth: [
        {
          title: t("lnb.sideBar.body.user.link"),
          url: pagePath.user.list,
          privilege: PartnerPrivilegeCode.CONTRACT,
        },
        {
          title: t("lnb.sideBar.body.visitor.link"),
          icon: "visitor",
          url: pagePath.visitor.list,
          privilege: PartnerPrivilegeCode.VISITOR,
          divider: true,
        },
      ],
      divider: true,
    },
    {
      title: t("lnb.sideBar.body.accessBooking.link"),
      icon: "reservation",
      openMenu: true,
      twoDepth: [
        {
          title: t("lnb.sideBar.body.accessControl.link"),
          url: pagePath.accessGroup.list,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.accessRecord.link"),
          url: pagePath.accessRecord.list,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.reservation.link"),
          icon: "reservation",
          url: pagePath.reservation.list,
          feature: PartnerFeature.RESERVATION,
          privilege: PartnerPrivilegeCode.RESERVATION,
        },
        {
          title: t("lnb.sideBar.body.commonSpaceSettlement.link"),
          icon: "reservation",
          url: pagePath.commonSpaceSettlement.list,
          privilege: PartnerPrivilegeCode.CONTRACT,
        },
        {
          title: t("lnb.sideBar.body.reservationConfig.link"),
          url: pagePath.reservationConfig.form,
          privilege: PartnerPrivilegeCode.BASIC,
        },
      ],
      divider: true,
    },
    {
      title: t("lnb.sideBar.body.operations.link"),
      icon: "question",
      openMenu: true,
      twoDepth: [
        {
          title: t("lnb.sideBar.body.workorder.link"),
          icon: "workorder",
          url: pagePath.workOrder.authenticated.list,
          feature: PartnerFeature.WORKORDER,
          privilege: PartnerPrivilegeCode.WORKORDER,
        },
        {
          title: t("lnb.sideBar.body.cs.link"),
          url: pagePath.question.list,
          feature: PartnerFeature.CS,
          privilege: PartnerPrivilegeCode.CS,
          divider: true,
        },
      ],
      divider: true,
    },

    {
      title: t("lnb.sideBar.body.info.link"),
      icon: "setting",
      openMenu: true,
      twoDepth: [
        {
          title: t("lnb.sideBar.body.notice.link"),
          icon: "notice",
          url: pagePath.notice.list,
          privilege: PartnerPrivilegeCode.NOTICE,
          divider: true,
        },
        {
          title: t("lnb.sideBar.body.guide.link"),

          url: pagePath.guide.list,
          // feature: PartnerFeature.CS,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.voc.link"),
          icon: "voc",
          url: pagePath.voc.list,
          feature: PartnerFeature.CS,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.userAdditionalInfo.link"),
          url: pagePath.userAdditionalInfo.list,
          feature: PartnerFeature.MEMBER_PARTNER_CUSTOM_INFO,
          privilege: PartnerPrivilegeCode.BASIC,
        },
        {
          title: t("lnb.sideBar.body.customLink.link"),
          url: pagePath.customLink.list,
          feature: PartnerFeature.CUSTOM_LINK,
        },
        {
          title: t("lnb.sideBar.body.setting.link"),
          url: pagePath.setting.form,
          privilege: PartnerPrivilegeCode.BASIC,
        },
      ],
    },
    //v.1.19 작업 > Harry 수정
    // {
    //   title: "관리비-부과항목관리",
    //   icon: "cost",
    //   url: pagePath.chargeItems.list,
    //   feature: PartnerFeature.MANAGEMENT_FEE,
    // },
    // {
    //   title: "관리비-부과관리",
    //   icon: "cost",
    //   url: pagePath.chargeMnt.list,
    //   feature: PartnerFeature.MANAGEMENT_FEE,
    //   divider: true,
    // },
  ]);

  // 파트너 접근허용 메뉴
  const { validRoutes } = useValidRoutes(sideBarMenuList);

  // 유효한 LNB 노출되는 목록 return
  const validSideBarMenuList = useMemo(() => {
    // partnerPrivileges가 빈 배열인지 확인
    const isEmptyPrivileges = !partnerPrivileges || partnerPrivileges.length === 0;

    // validRoutes를 한 번만 순회하며 필요한 필터링 적용
    return validRoutes
      ?.map((route: Menu) => {
        // twoDepth 메뉴 필터링
        if (route.twoDepth && route.twoDepth.length > 0) {
          return {
            ...route,
            twoDepth: route.twoDepth.filter((subMenu) => {
              // 권한이 있는 경우만 필터링
              if (subMenu.privilege && !isEmptyPrivileges) {
                const hasPrivilege = partnerPrivileges.some(
                  (privilege) => privilege.code === subMenu.privilege && privilege.permissions.includes(PartnerPermission.READ),
                );
                return hasPrivilege;
              }
              // partnerPrivileges가 빈 배열이면 모든 메뉴 표시
              // 또는 privilege가 없는 메뉴는 그대로 표시
              return true;
            }),
          };
        }
        return route;
      })
      .filter((item) => {
        // 상위 메뉴에 권한 체크가 필요한 경우
        if (item.privilege && !isEmptyPrivileges) {
          const hasPrivilege = partnerPrivileges.some(
            (privilege) => privilege.code === item.privilege && privilege.permissions.includes(PartnerPermission.READ),
          );
          return hasPrivilege;
        }

        // partnerPrivileges가 빈 배열이면 모든 메뉴 표시
        if (isEmptyPrivileges) {
          return true;
        }

        // twoDepth가 있는 경우 하위 메뉴가 하나라도 있으면 표시
        if (item.twoDepth && item.twoDepth.length > 0) {
          return item.twoDepth.length > 0;
        }
        return true; // 그 외 경우는 모두 표시
      });
  }, [partnerPrivileges, validRoutes]);

  // 부모 div에 active-parent 클래스 추가하는 함수

  // 권한없는 페이지로 url 접근시 block

  useEffect(() => {
    // 권한 조회 완료 전(빈 privileges)이나 메뉴 세그먼트 계산 전에는 판단하지 않음
    // URL 직접 진입 시 페이지가 마운트 직후 navigate(replace)하면 권한 응답 전에 차단 알럿이 뜨던 문제 (QA-3830)
    if (authorityStatus !== "success" || !currentMenu) return;

    // 현재 page의 path(url)
    const currentPagePath = location.pathname + location.search;

    //현재 페이지의 권한 코드 > "BASIC" | "BUILDING" | "PRODUCT" |"CS" |"CONTRACT" 등 return
    let currentPagePrivilegeCode = "";

    for (let key in PrivilegeCodeMenus) {
      const menus = PrivilegeCodeMenus[key as UnionPartnerPrivilegeCode];
      if (menus.includes(currentMenu)) {
        currentPagePrivilegeCode = key;
        break;
      }
    }
    // 파트너의 권한 확인 > {code: "BASIC" ,permissions:['d', 'w', 'r']} return
    const partnerAuthority = partnerPrivileges.find((privilege) => privilege.code === currentPagePrivilegeCode);

    let isBlocked = false;

    // read 가능여부 (권한 코드 자체가 없거나, r 권한이 없으면 차단)
    const readalbe = currentPagePrivilegeCode && (!partnerAuthority || !partnerAuthority.permissions.includes("r"));

    // add / edit 가능여부 (권한 코드 자체가 없거나, w 권한이 없으면 차단)
    const editable =
      currentPagePath.includes("form") && currentPagePrivilegeCode && (!partnerAuthority || !partnerAuthority.permissions.includes("w"));
    if (readalbe) {
      isBlocked = true;
    }
    if (editable) {
      isBlocked = true;
    }
    isBlocked && fetchAuthorityReadonly(true, true);
  }, [partnerPrivileges, location, currentMenu, authorityStatus]);

  useEffect(() => {
    // grecaptcha-badge 클래스를 가진 요소를 찾아서 스타일 변경
    const grecaptchaBadge = document.querySelector(".grecaptcha-badge");

    if (grecaptchaBadge) {
      if (isMobilePath(location.pathname)) {
        (grecaptchaBadge as HTMLElement).style.display = "none";
      }
    }

    // 컴포넌트가 언마운트될 때 원래 상태로 복원하려면 (선택적)
    return () => {
      const badge = document.querySelector(".grecaptcha-badge");
      if (badge) {
        (badge as HTMLElement).style.display = "block"; // 또는 원래 값으로 복원
      }
    };
  }, []); // 빈 의존성 배열로 마운트 시 한 번만 실행
  if (!isAuthenticated) {
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks

  const hasActiveChild = (menu: Menu) => {
    return menu.twoDepth?.some((item) => {
      return item.url?.split("/")[5] === currentMenu;
    });
  };

  return (
    <div className="sidebar">
      <div className="sidebar__logo-wrap " onClick={() => !isMobilePath(location.pathname) && navigate(pagePath.main)}>
        <div className="sidebar__ctrl-logo"></div>
        {!isMobilePath(location.pathname) && partner?.logoImage ? (
          <div style={{ backgroundImage: `url(${partner?.logoImage?.url})` }} className="sidebar__partner-logo"></div>
        ) : (
          <h1 className="sidebar__logo-title ">{partner?.name}</h1>
        )}
      </div>
      {!isMobilePath(location.pathname) && (
        <>
          <nav className="h-100">
            <ul>
              {/* smartThings 단독 */}
              {partner?.features?.find((f: { name: string; active: boolean }) => f.name === "CTRLROOM_NAVIGATION_STPRO")?.active && (
                <li className="mb0">
                  <p>
                    <NavLink className="pl0 " to={pagePath.smartThingsPro.replace(":partnerId", String(partner?.id))}>
                      <span className="font16 font-weight-semibold no-wrap">{t("lnb.sideBar.body.smartthings.link")}</span>
                      <span className={"rotate-menu-chevron ic-chevron-down w-100 text-right mr20"}></span>
                    </NavLink>
                  </p>
                </li>
              )}
              {validSideBarMenuList?.map((menu: Menu, idx) => {
                return (
                  <li key={idx} className={menu.icon}>
                    <>
                      {menu.twoDepth ? (
                        <>
                          <p
                            onClick={() =>
                              setSideBarMenuList((prev) =>
                                prev.map((item) => ({ ...item, openMenu: menu.title === item.title ? !item.openMenu : item.openMenu })),
                              )
                            }
                          >
                            {menu.title !== "SmartThings Pro" && <span className="icon"></span>}
                            <span className="w-100 font16 font-weight-semibold">{menu.title}</span>
                            <span className={`${menu.openMenu ? "ic-chevron-up" : "ic-chevron-down"} w-100 text-right mr20`}></span>
                          </p>
                          {menu.openMenu && (
                            <>
                              <div className={`two-depth-menu ${hasActiveChild(menu) ? "active-parent" : ""}`}>
                                {menu.twoDepth.map((twoDepthMenu: Menu, index: number) => {
                                  return (
                                    <NavLink
                                      to={twoDepthMenu.url ? twoDepthMenu.url.replace(":partnerId", String(partner?.id)) : "/" + twoDepthMenu.title}
                                      key={index + twoDepthMenu.title}
                                    >
                                      <span className="font16">{twoDepthMenu.title}</span>
                                    </NavLink>
                                  );
                                })}
                                {!hasActiveChild(menu) && menu.divider ? <div className="sidebar__divider"></div> : null}
                              </div>
                              {hasActiveChild(menu) && menu.divider ? <div className="pt10" style={{ backgroundColor: "transparent" }}></div> : null}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <NavLink to={menu.url ? menu.url.replace(":partnerId", String(partner?.id)) : "/" + menu.title} key={idx}>
                            <span className="icon"></span>
                            <span className="font16">{menu.title}</span>
                          </NavLink>
                          {/* {menu.divider && <div className="sidebar__divider"></div>} */}
                        </>
                      )}
                      {menu.openMenu === false && menu.divider && <div className="sidebar__divider"></div>}
                    </>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div>
            <div className="sidebar__manual">
              <button
                className="text-center"
                onClick={() => window.open("https://pnpt.atlassian.net/wiki/external/ZjliYmFhNTY5NzliNDU0MmIwNGNkODQzMDc3ZWIzNDc", "_blank")}
              >
                {t("lnb.sideBar.body.help.link")}
              </button>
            </div>
            <div className="sidebar__footer">
              {/* <p className="mb4">{partner?.name}</p> */}
              {partnerMember?.lastLoginDate ? (
                <span>
                  {t("lnb.sideBar.body.login")} {moment(partnerMember?.lastLoginDate).format(ViewYmdFormat.YYYY_MM_DD_HH_MM)}
                </span>
              ) : (
                <span>{t("lnb.sideBar.body.login")}: 없음</span>
              )}
              <p className="font11">© Powered by PINPOINT</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
